import { createClient } from '@sanity/client';
import { createImageUrlBuilder } from '@sanity/image-url';
import localProducts from '../data/products';

// Environment credentials strictly from environment variables
export const SANITY_PROJECT_ID =
  process.env.REACT_APP_SANITY_PROJECT_ID ||
  process.env.REACT_APP_SANITY_PROJECT;

export const SANITY_DATASET =
  process.env.REACT_APP_SANITY_DATASET ||
  'production';

export const isSanityConfigured = Boolean(
  SANITY_PROJECT_ID && /^[a-z0-9-]+$/.test(SANITY_PROJECT_ID)
);

// Initialize Sanity Client safely
export const client = isSanityConfigured
  ? createClient({
      projectId: SANITY_PROJECT_ID,
      dataset: SANITY_DATASET,
      apiVersion: '2024-01-01',
      useCdn: true,
    })
  : null;

// Image Builder with fallback for local path strings
const builder = client ? createImageUrlBuilder(client) : null;

export const urlFor = (source) => {
  if (!source) {
    return {
      url: () => 'https://images.unsplash.com/photo-1572375992501-4b0892d50c69?q=80&w=600&auto=format&fit=crop',
      width: () => ({ auto: () => ({ url: () => '' }) }),
      auto: () => ({ url: () => '' }),
    };
  }

  // If already a direct string URL or local path
  if (typeof source === 'string') {
    return {
      url: () => source,
      width: () => ({ auto: () => ({ url: () => source }) }),
      auto: () => ({ url: () => source }),
    };
  }

  // Sanity image asset using builder
  if (builder) {
    try {
      return builder.image(source);
    } catch (err) {
      // fallback to manual URL
    }
  }

  // Direct CDN URL builder fallback
  if (source?.asset?._ref) {
    const ref = source.asset._ref;
    const parts = ref.split('-');
    if (parts.length >= 4) {
      const hash = parts[1];
      const dimensions = parts[2];
      const ext = parts[3];
      const pId = SANITY_PROJECT_ID || 'me8ag9en';
      const dSet = SANITY_DATASET || 'production';
      const cdnUrl = `https://cdn.sanity.io/images/${pId}/${dSet}/${hash}-${dimensions}.${ext}`;
      return {
        url: () => cdnUrl,
        width: () => ({ auto: () => ({ url: () => cdnUrl }) }),
        auto: () => ({ url: () => cdnUrl }),
      };
    }
  }

  return {
    url: () => '',
    width: () => ({ auto: () => ({ url: () => '' }) }),
    auto: () => ({ url: () => '' }),
  };
};

/**
 * Fetch all products from Sanity, with seamless fallback to local products.
 */
export async function getProducts() {
  if (!isSanityConfigured) {
    return localProducts;
  }

  try {
    const query = `*[_type == "product" && (isActive == true || !defined(isActive))] | order(_createdAt desc) {
      _id,
      name,
      slug,
      price,
      squareVariationId,
      squareCatalogId,
      itemType,
      tags,
      types[] {
        name,
        image,
        squareVariationId,
        inStock
      },
      category,
      description,
      coverImage,
      gallery,
      isCustomizable,
      options,
      inStock,
      isActive
    }`;

    const sanityData = await client.fetch(query);

    if (!sanityData || sanityData.length === 0) {
      return localProducts;
    }

    // Adapt Sanity schema to website product format
    const formattedSanity = sanityData.map((doc, idx) => {
      // Smart price format & numeric price for accurate sorting
      let formattedPrice = '$10';
      let numericPrice = 10;
      if (typeof doc.price === 'number') {
        if (doc.price >= 100) {
          numericPrice = doc.price / 100;
          formattedPrice = doc.price % 100 === 0 ? `$${doc.price / 100}` : `$${(doc.price / 100).toFixed(2)}`;
        } else {
          numericPrice = doc.price;
          formattedPrice = `$${doc.price}`;
        }
      } else if (doc.price) {
        const cleaned = parseFloat(String(doc.price).replace(/[^0-9.]+/g, '')) || 0;
        numericPrice = cleaned;
        formattedPrice = String(doc.price).startsWith('$') ? doc.price : `$${doc.price}`;
      }

      const coverUrl = doc.coverImage ? urlFor(doc.coverImage).width(800).auto('format').url() : localProducts[0].image;

      const formattedTypes = Array.isArray(doc.types) && doc.types.length > 0
        ? doc.types.map(t => ({
            name: t.name,
            image: t.image ? urlFor(t.image).width(1000).auto('format').url() : null,
            squareVariationId: t.squareVariationId || null,
            inStock: t.inStock ?? true,
          }))
        : [];
      // Gather gallery from doc.gallery and also from formattedTypes
      const initialGallery = Array.isArray(doc.gallery) && doc.gallery.length > 0
        ? doc.gallery.map(g => (g.photo ? urlFor(g.photo).width(1000).auto('format').url() : coverUrl))
        : [coverUrl];
      const initialDetails = Array.isArray(doc.gallery) && doc.gallery.length > 0
        ? doc.gallery.map(g => ({
            url: g.photo ? urlFor(g.photo).width(1000).auto('format').url() : coverUrl,
            label: g.label || doc.name
          }))
        : [{ url: coverUrl, label: doc.name }];

      // Include type images in gallery if not already present
      formattedTypes.forEach(t => {
        if (t.image && !initialGallery.includes(t.image)) {
          initialGallery.push(t.image);
          initialDetails.push({ url: t.image, label: t.name });
        }
      });

      const options = doc.options ? { ...doc.options } : {};
      if (formattedTypes.length > 0) {
        if (!options.featuredMembers) {
          options.featuredMembers = formattedTypes.map(t => t.name);
        }
        if (!options.featuredThemes) {
          options.featuredThemes = formattedTypes.map(t => t.name);
        }
      }

      return {
        id: doc._id || `sanity-${idx + 1}`,
        slug: doc.slug?.current || `product-${idx + 1}`,
        name: doc.name || 'Unnamed Product',
        price: formattedPrice,
        priceNum: numericPrice,
        squareVariationId: doc.squareVariationId || null,
        squareCatalogId: doc.squareCatalogId || null,
        itemType: doc.itemType || (doc.isCustomizable ? 'custom' : 'ready-to-ship'),
        tags: Array.isArray(doc.tags) ? doc.tags : [],
        category: doc.category || 'Misc',
        image: coverUrl,
        types: formattedTypes,
        gallery: initialGallery,
        galleryDetails: initialDetails,
        description: doc.description || '',
        isCustomizable: Boolean(doc.isCustomizable),
        options: options,
        inStock: doc.inStock ?? true,
        isFromSanity: true,
      };
    });

    // Merge: Sanity products take precedence, existing local catalog fills the rest
    const sanityNames = new Set(formattedSanity.map(p => p.name.trim().toLowerCase()));
    const remainingLocal = localProducts.filter(p => !sanityNames.has(p.name.trim().toLowerCase()));

    return [...formattedSanity, ...remainingLocal];
  } catch (err) {
    console.warn('Could not reach Sanity API, using local product catalog:', err.message);
    return localProducts;
  }
}

/**
 * Fetch site-wide announcement and settings, with fallback
 */
export async function getSiteSettings() {
  const defaultSettings = {
    announcementBar: 'Free shipping on US sticker & photocard orders! • Handcrafted with love in Hawaii',
    shopIsOpen: true,
    processingTime: '3–7 business days',
    aboutText: 'Star.Ryhwa is an indie creative studio based in Hawaii specializing in handcrafted K-Pop collages, customized accessories, and fandom goodies.',
  };

  if (!isSanityConfigured) {
    return defaultSettings;
  }

  try {
    const data = await client.fetch(`*[_type == "siteSettings"][0]`);
    return data ? { ...defaultSettings, ...data } : defaultSettings;
  } catch (err) {
    return defaultSettings;
  }
}
